import { createAdSize } from '@guardian/commercial-core/ad-sizes';
import { PREBID_TIMEOUT } from '@guardian/commercial-core/constants/prebid-timeout';
import { hashEmailForClient } from '@guardian/commercial-core/email-hash';
import { EventTimer } from '@guardian/commercial-core/event-timer';
import { getPermutiveSegments } from '@guardian/commercial-core/permutive';
import { getConsentFor, isString, log, onConsent } from '@guardian/libs';
import { flatten } from 'lodash-es';
import { getParticipations } from '../../../experiments/ab';
import { isUserInTestGroup } from '../../../experiments/beta-ab';
import { pubmatic } from '../../__vendor/pubmatic';
import { getAdvertById } from '../../dfp/get-advert-by-id';
import { getEmail, isUserLoggedIn } from '../../identity/api';
import { getPageTargeting } from '../../page-targeting';
import { getHeaderBiddingAdSlots } from '../slot-config';
import { isSwitchedOn, shouldIncludeBidder, shouldIncludePermutive, stripDfpAdPrefixFrom, } from '../utils';
import { bids } from './bid-config';
import { overridePriceBucket, priceGranularity } from './price-config';
class PrebidAdUnit {
    code;
    bids;
    mediaTypes;
    gpid;
    ortb2Imp;
    constructor(advert, slot, pageTargeting, consentState) {
        this.code = advert.id;
        this.mediaTypes = { banner: { sizes: slot.sizes } };
        this.gpid = advert.gpid ?? '';
        this.ortb2Imp = {
            ext: {
                gpid: this.gpid,
                data: {
                    pbadslot: this.gpid,
                },
            },
        };
        this.bids = bids(advert.id, slot.sizes, pageTargeting, this.gpid, consentState);
        advert.headerBiddingSizes = slot.sizes;
        log('commercial', `PrebidAdUnit ${this.code}`, this.bids);
    }
    isEmpty() {
        return this.code == null;
    }
}
const shouldEnableAnalytics = () => {
    if (!window.guardian.config.switches.prebidAnalytics) {
        return false;
    }
    const analyticsSampleRate = 10 / 100;
    const isInSample = Math.random() < analyticsSampleRate;
    const isInServerSideTest = Object.keys(window.guardian.config.tests ?? {}).length > 0;
    const isInClientSideTest = Object.keys(getParticipations()).length > 0;
    /**
     * @todo drop old client/server side checks and use just window.guardian.modules.abTests once
     * all tests have been migrated to the new AB testing platform
     */
    const isInNewABTest = Object.keys(window.guardian.modules.abTests?.getParticipations() ?? {})
        .length > 0;
    const hasQueryParam = window.location.search.includes('pbjs-analytics=true');
    return (isInServerSideTest ||
        isInClientSideTest ||
        isInNewABTest ||
        isInSample ||
        hasQueryParam);
};
const getTheTradeDeskIdParams = async (id, email) => {
    const emailHash = await hashEmailForClient(email, id);
    if (id === 'uid2') {
        return {
            name: 'uid2',
            params: {
                serverPublicKey: 'UID2-X-P-MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE7MS+2jntlSNTDP65WBYaCLR/Wla8r3h9NkYtN73lNtbo7WT5LFIKSGnD0kERa8VG8bNJvZrQs2bCU0P8ZH4uaA==',
                subscriptionId: 'HhGv3vmQcS',
                emailHash,
            },
        };
    }
    return {
        name: 'euid',
        params: {
            serverPublicKey: 'EUID-X-P-MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEyoVAEgz82CK4G25Y1wGUngy3g9o3kCpl5bWTtCAJAx5gpG4PvhEaTPWCRp+FVVAzvkocZ/1IUJ4wPoS/QdIe5w==',
            subscriptionId: 'SvB8xb94yD',
            emailHash,
        },
    };
};
/**
 * Prebid supports an additional timeout buffer to account for noisiness in
 * timing JavaScript on the page. This value is passed to the Prebid config
 * and is adjustable via this constant
 */
const timeoutBuffer = 400;
/**
 * The amount of time reserved for the auction
 */
const bidderTimeout = PREBID_TIMEOUT;
let requestQueue = Promise.resolve();
let initialised = false;
const initialise = async (window, consentState) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ignore during v10 test
    if (!window.pbjs) {
        log('commercial', 'window.pbjs not found on window');
        return; // We couldn’t initialise
    }
    initialised = true;
    const userIds = [
        {
            name: 'sharedId',
            storage: {
                type: 'cookie',
                name: '_pubcid',
                expires: 365,
            },
        },
    ];
    if (getConsentFor('id5', consentState)) {
        const id5UserId = {
            name: 'id5Id',
            params: {
                partner: 182,
                // ID5 recommend specifying this to ensure use of the most recent module version
                // https://wiki.id5.io/docs/id5-prebid-user-id-module
                externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
            },
            storage: {
                type: 'html5',
                name: 'id5id',
                expires: 90,
                refreshInSeconds: 7200,
            },
        };
        const email = await getEmail();
        if (email &&
            !isUserInTestGroup('commercial-user-module-ID5', 'variant')) {
            const hashedEmail = await hashEmailForClient(email, 'id5');
            const pdRaw = new URLSearchParams([['1', hashedEmail]]).toString();
            const pdString = btoa(pdRaw);
            userIds.push({
                ...id5UserId,
                params: {
                    ...id5UserId.params,
                    pd: pdString,
                },
            });
        }
        else {
            userIds.push(id5UserId);
        }
    }
    const isInTheTradeDeskIdTest = !isUserInTestGroup('commercial-user-module-uid2', 'variant');
    const isValidFrameworkForTheTradeDeskId = consentState.framework &&
        ['tcfv2', 'usnat'].includes(consentState.framework);
    if (getConsentFor('theTradeDesk', consentState)) {
        const email = await getEmail();
        if (email &&
            isInTheTradeDeskIdTest &&
            isValidFrameworkForTheTradeDeskId) {
            const idType = consentState.framework === 'tcfv2' ? 'euid' : 'uid2';
            const params = await getTheTradeDeskIdParams(idType, email);
            userIds.push(params);
        }
    }
    const userSync = isSwitchedOn('prebidUserSync')
        ? {
            syncsPerBidder: 0, // allow all syncs
            filterSettings: {
                all: {
                    bidders: '*', // allow all bidders to sync by iframe or image beacons
                    filter: 'include',
                },
            },
            userIds,
        }
        : { syncEnabled: false };
    const consentManagement = () => {
        switch (consentState.framework) {
            /** @see https://docs.prebid.org/dev-docs/modules/consentManagementUsp.html */
            case 'aus':
                return {
                    usp: {
                        cmpApi: 'iab',
                        timeout: 1500,
                    },
                };
            /** @see https://docs.prebid.org/dev-docs/modules/consentManagementGpp.html */
            case 'usnat':
                return {
                    gpp: {
                        cmpApi: 'iab',
                        timeout: 1500,
                    },
                };
            /** @see https://docs.prebid.org/dev-docs/modules/consentManagementTcf.html */
            case 'tcfv2':
            default:
                return {
                    gdpr: {
                        cmpApi: 'iab',
                        timeout: 200,
                        defaultGdprScope: true,
                    },
                };
        }
    };
    /** Helper function to decide if a bidder should be included.
     * It is a curried function prepared with the consent state
     * at the time of initialisation to avoid unnecessary repetition
     * of consent state throughout */
    const shouldInclude = shouldIncludeBidder(consentState);
    const pbjsConfig = Object.assign({}, {
        bidderTimeout,
        timeoutBuffer,
        priceGranularity,
        userSync,
    });
    const keywordsArray = window.guardian.config.page.keywords.split(',');
    pbjsConfig.ortb2 = {
        site: {
            ext: {
                data: {
                    keywords: keywordsArray,
                },
            },
        },
    };
    window.pbjs.bidderSettings = {};
    if (isSwitchedOn('consentManagement')) {
        pbjsConfig.consentManagement = consentManagement();
    }
    if (shouldIncludePermutive(consentState)) {
        const includedAcBidders = [
            'and',
            'ix',
            'ozone',
            'pubmatic',
            'trustx',
            'rubicon',
        ]
            .filter(shouldInclude)
            // "and" is the alias for the custom Guardian "appnexus" direct bidder
            .map((bidder) => (bidder === 'and' ? 'appnexus' : bidder));
        pbjsConfig.realTimeData = {
            dataProviders: [
                {
                    name: 'permutive',
                    params: {
                        acBidders: includedAcBidders,
                        ...(includedAcBidders.includes('pubmatic')
                            ? { overwrites: { pubmatic } }
                            : {}),
                    },
                },
            ],
        };
    }
    if (shouldInclude('criteo')) {
        window.pbjs.bidderSettings.criteo = {
            storageAllowed: true,
            // Use a custom price granularity, which is based upon the size of the slot being auctioned
            adserverTargeting: [
                {
                    key: 'hb_pb',
                    val({ width, height, cpm, pbCg }) {
                        return overridePriceBucket('criteo', width, height, cpm, pbCg);
                    },
                },
            ],
        };
    }
    if (shouldInclude('ozone')) {
        // Use a custom price granularity, which is based upon the size of the slot being auctioned
        window.pbjs.bidderSettings.ozone = {
            adserverTargeting: [
                {
                    key: 'hb_pb',
                    val: ({ width, height, cpm, pbCg }) => {
                        return overridePriceBucket('ozone', width, height, cpm, pbCg);
                    },
                },
            ],
        };
    }
    if (shouldInclude('ix')) {
        // Use a custom price granularity, which is based upon the size of the slot being auctioned
        window.pbjs.bidderSettings.ix = {
            adserverTargeting: [
                {
                    key: 'hb_pb',
                    val({ width, height, cpm, pbCg }) {
                        return overridePriceBucket('ix', width, height, cpm, pbCg);
                    },
                },
            ],
        };
    }
    if (shouldEnableAnalytics() && window.guardian.ophan?.pageViewId) {
        window.pbjs.enableAnalytics([
            {
                provider: 'gu',
                options: {
                    url: window.guardian.config.page.isDev ||
                        window.location.hostname.includes('localhost')
                        ? `//performance-events.code.dev-guardianapis.com/header-bidding`
                        : `//performance-events.guardianapis.com/header-bidding`,
                    pv: window.guardian.ophan.pageViewId,
                },
            },
        ]);
    }
    if (shouldInclude('xhb')) {
        window.pbjs.bidderSettings.xhb = {
            adserverTargeting: [
                {
                    key: 'hb_buyer_id',
                    val(bidResponse) {
                        // TODO: should we return null or an empty string?
                        return bidResponse.appnexus?.buyerMemberId ?? '';
                    },
                },
            ],
            bidCpmAdjustment: (bidCpm) => {
                return bidCpm * 1.05;
            },
        };
    }
    if (shouldInclude('kargo')) {
        window.pbjs.bidderSettings.kargo = {
            storageAllowed: true,
        };
    }
    if (shouldInclude('rubicon')) {
        window.pbjs.bidderSettings.magnite = {
            storageAllowed: true,
        };
        window.pbjs.setBidderConfig({
            bidders: ['rubicon'],
            config: {
                ortb2: {
                    user: {
                        ext: {
                            data: {
                                permutive: getPermutiveSegments(),
                            },
                        },
                    },
                },
            },
        });
    }
    window.pbjs.setConfig(pbjsConfig);
    // Adjust slot size when prebid ad loads
    window.pbjs.onEvent('bidWon', (data) => {
        const { width, height, adUnitCode } = data;
        if (!width || !height || !adUnitCode) {
            return;
        }
        const size = createAdSize(width, height); // eg. [300, 250]
        const advert = getAdvertById(adUnitCode);
        if (!advert) {
            return;
        }
        advert.size = size;
        /**
         * when hasPrebidSize is true we use size
         * set here when adjusting the slot size.
         * */
        advert.hasPrebidSize = true;
    });
};
const bidsBackHandler = (adUnits, eventTimer) => new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ignore during v10 test
    window.pbjs?.setTargetingForGPTAsync(adUnits.map((u) => u.code).filter(isString));
    resolve();
    adUnits.forEach((adUnit) => {
        if (isString(adUnit.code)) {
            eventTimer.mark('prebidEnd', stripDfpAdPrefixFrom(adUnit.code));
        }
    });
});
// slotFlatMap allows you to dynamically interfere with the PrebidSlot definition
// for this given request for bids.
const requestBids = async (adverts, slotFlatMap) => {
    if (!initialised) {
        return requestQueue;
    }
    if (!window.guardian.config.switches.prebidHeaderBidding) {
        return requestQueue;
    }
    const adUnits = await onConsent()
        .then(async (consentState) => {
        // calculate this once before mapping over
        const isSignedIn = await isUserLoggedIn();
        const pageTargeting = getPageTargeting(consentState, isSignedIn);
        return flatten(adverts.map((advert) => getHeaderBiddingAdSlots(advert, slotFlatMap)
            .map((slot) => new PrebidAdUnit(advert, slot, pageTargeting, consentState))
            .filter((adUnit) => !adUnit.isEmpty())));
    })
        .catch((e) => {
        // silently fail
        log('commercial', 'Failed to execute prebid onConsent', e);
        return [];
    });
    const eventTimer = EventTimer.get();
    requestQueue = requestQueue.then(() => new Promise((resolve) => {
        adUnits.forEach((adUnit) => {
            if (isString(adUnit.code)) {
                eventTimer.mark('prebidStart', stripDfpAdPrefixFrom(adUnit.code));
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ignore during v10 test
        window.pbjs?.que.push(() => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ignore during v10 test
            window.pbjs?.requestBids({
                adUnits,
                bidsBackHandler: () => void bidsBackHandler(adUnits, eventTimer).then(resolve),
            });
        });
    }));
    return requestQueue;
};
export const prebid = { initialise, requestBids };
